import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  TextField,
  Typography,
} from "@mui/material";

import type { BilledPeriods } from "@models/population.model";

interface Props {
  open: boolean;
  billed: BilledPeriods | null;
  onCancel: () => void;
  onConfirm: (reason: string) => void;
}

/*
    Confirmation for reprocessing a population whose months have already been
    invoiced (MPN-1516).

    Deliberately not the window.confirm the unbilled path uses. The ticket asks
    for the override to be a distinct action rather than the same single click,
    and a reason is mandatory because it IS the audit record for changing a
    billed number — so the confirm button stays disabled until both the
    acknowledgement and a non-empty reason are given, rather than letting the
    server reject an empty one after the fact.
*/
export default function BilledPeriodOverrideDialog({ open, billed, onCancel, onConfirm }: Props) {
  const [acknowledged, setAcknowledged] = useState(false);
  const [reason, setReason] = useState("");

  // Never carry a previous override's reason into the next one.
  useEffect(() => {
    if (open) {
      setAcknowledged(false);
      setReason("");
    }
  }, [open]);

  const months = billed?.months ?? [];
  const ready = acknowledged && reason.trim().length > 0;

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontSize: 16, fontWeight: 600, color: "#232323" }}>
        This population has already been billed
      </DialogTitle>
      <DialogContent>
        <Alert severity="warning" sx={{ fontSize: 13 }}>
          Reprocessing rebuilds conversions from scratch, which changes what the client was
          billed for {months.length === 1 ? "this month" : "these months"}:
          <Box component="ul" sx={{ mb: 0, mt: 1, pl: 2.5 }}>
            {months.map((m) => (
              <li key={m.start}>
                <strong>{m.label}</strong> — {m.conversions.toLocaleString()} billable conversions
              </li>
            ))}
          </Box>
        </Alert>

        <Typography sx={{ fontSize: 13, color: "#8c9097", mt: 2 }}>
          The current invoice for {months.length === 1 ? "that month" : "those months"} is saved
          before anything is deleted, so the original numbers stay retrievable and can be compared
          against the rebuilt ones.
        </Typography>

        <FormControlLabel
          sx={{ mt: 1, alignItems: "flex-start" }}
          control={
            <Checkbox
              checked={acknowledged}
              onChange={(e) => setAcknowledged(e.target.checked)}
              size="small"
            />
          }
          label={
            <Typography sx={{ fontSize: 13, mt: 0.5 }}>
              I am correcting a confirmed billing error and mean to rewrite{" "}
              {months.length === 1 ? "this month" : "these months"}.
            </Typography>
          }
        />

        <TextField
          fullWidth
          multiline
          rows={3}
          size="small"
          label="Reason for the correction"
          placeholder="Recorded against the job and the saved invoice."
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          disabled={!acknowledged}
          sx={{ mt: 1 }}
          inputProps={{ style: { fontSize: 13 } }}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onCancel} size="small" color="inherit">
          Cancel
        </Button>
        <Button
          onClick={() => onConfirm(reason.trim())}
          size="small"
          variant="contained"
          color="warning"
          disabled={!ready}
        >
          Reprocess anyway
        </Button>
      </DialogActions>
    </Dialog>
  );
}
